import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { taskEffort, wsSummary, fmtH, fmtC } from "@/lib/calc";
import { ChevronDown, ChevronRight, GripVertical } from "lucide-react";
import { toast } from "sonner";

const CELL = 44;
const PALETTE = ["#002FA7", "#0E7490", "#7C3AED", "#B45309", "#BE185D", "#047857", "#4338CA", "#9F1239"];

// Sequential phase segments within a workstream span, proportional to epic hours
function phaseSegments(est, ws) {
  const order = [], map = {};
  for (const t of (est.tasks || []).filter((t) => t.workstream === ws.name)) {
    const ep = t.epic || "General";
    if (!(ep in map)) { map[ep] = 0; order.push(ep); }
    map[ep] += taskEffort(t, est.tshirt_sizes) * (1 + (Number(t.contingency_pct) || 0));
  }
  const total = order.reduce((s, e) => s + map[e], 0);
  let acc = 0;
  return order.map((ep) => {
    const f0 = total ? acc / total : 0;
    acc += map[ep];
    return { name: ep, hours: map[ep], f0, f1: total ? acc / total : 1 };
  });
}

export default function TimelineTab({ est, setEst, oppId }) {
  const numWeeks = Math.max(1, Number(est.num_weeks) || 12);
  const [local, setLocal] = useState(null); // {name, start, end} while dragging
  const localRef = useRef(null);
  const [collapsed, setCollapsed] = useState({});
  const summaries = wsSummary(est);

  const setDrag = (v) => { localRef.current = v; setLocal(v); };

  const weekDate = (i) => {
    if (!est.start_date) return null;
    const d = new Date(est.start_date + "T00:00:00");
    d.setDate(d.getDate() + i * 7);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const todayWeek = (() => {
    if (!est.start_date) return null;
    const w = Math.floor((Date.now() - new Date(est.start_date + "T00:00:00").getTime()) / (7 * 86400000));
    return w >= 0 && w < numWeeks ? w : null;
  })();

  const commit = async () => {
    const l = localRef.current;
    setDrag(null);
    if (!l) return;
    const ws = est.workstreams.find((w) => w.name === l.name);
    if (!ws || (ws.start_week === l.start && ws.end_week === l.end)) return;
    const prev = est;
    const newWs = est.workstreams.map((w) => (w.name === l.name ? { ...w, start_week: l.start, end_week: l.end } : w));
    setEst({ ...est, workstreams: newWs });
    try {
      const { data } = await api.put(`/opportunities/${oppId}/estimation/workstreams`, { workstreams: newWs });
      setEst(data);
      toast.success(`${l.name}: W${l.start}–W${l.end}`);
    } catch (e) {
      setEst(prev);
      toast.error(apiError(e));
    }
  };

  const beginDrag = (e, ws, mode) => {
    e.preventDefault();
    const startX = e.clientX;
    const orig = { start: ws.start_week, end: ws.end_week };
    setDrag({ name: ws.name, start: orig.start, end: orig.end });
    const onMove = (ev) => {
      const dw = Math.round((ev.clientX - startX) / CELL);
      let s = orig.start, en = orig.end;
      if (mode === "move") {
        s += dw; en += dw;
        if (s < 1) { en += 1 - s; s = 1; }
        if (en > numWeeks) { s -= en - numWeeks; en = numWeeks; s = Math.max(1, s); }
      } else if (mode === "left") {
        s = Math.min(Math.max(1, orig.start + dw), en);
      } else {
        en = Math.max(Math.min(numWeeks, orig.end + dw), s);
      }
      setDrag({ name: ws.name, start: s, end: en });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      commit();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const gridBg = {
    backgroundImage: `repeating-linear-gradient(to right, transparent 0 ${CELL - 1}px, #f1f1f3 ${CELL - 1}px ${CELL}px)`,
    width: numWeeks * CELL,
  };
  const todayLine = todayWeek !== null && (
    <div className="absolute top-0 bottom-0 w-px bg-red-400/70 pointer-events-none" style={{ left: todayWeek * CELL + CELL / 2 }} />
  );

  if (!est.workstreams?.length) {
    return <p data-testid="timeline-empty" className="text-sm text-zinc-500 p-4 bg-white border border-zinc-200 rounded-sm">
      No workstreams yet — generate or build a WBS first, then plan the timeline here.</p>;
  }

  return (
    <div data-testid="timeline-tab">
      <p className="text-xs text-zinc-500 mb-3">
        Drag a bar to shift a workstream; drag its edges to change start/end week. Phases are split by epic, proportional to hours.
      </p>
      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto select-none">
        <div style={{ width: 256 + numWeeks * CELL }}>
          {/* week header */}
          <div className="flex border-b border-zinc-300 bg-zinc-50">
            <div className="w-64 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium border-r border-zinc-200">
              Workstream / Phase
            </div>
            <div className="relative flex" style={{ width: numWeeks * CELL }}>
              {Array.from({ length: numWeeks }).map((_, i) => (
                <div key={`wk-${i}`} style={{ width: CELL }} className={`py-1.5 text-center ${i > 0 ? "border-l border-zinc-200" : ""}`}>
                  <p className="text-[10px] font-mono text-zinc-600">W{i + 1}</p>
                  {est.start_date && <p className="text-[9px] font-mono text-zinc-400">{weekDate(i)}</p>}
                </div>
              ))}
              {todayLine}
            </div>
          </div>

          {est.workstreams.map((ws, wi) => {
            const color = PALETTE[wi % PALETTE.length];
            const eff = local?.name === ws.name ? local : { start: ws.start_week, end: ws.end_week };
            const span = Math.max(1, eff.end - eff.start + 1);
            const sum = summaries.find((s) => s.name === ws.name);
            const segs = phaseSegments(est, ws);
            const isCollapsed = !!collapsed[ws.name];
            return (
              <div key={ws.name} className="border-b border-zinc-100 last:border-b-0">
                {/* workstream row */}
                <div className="flex">
                  <div className="w-64 shrink-0 px-3 py-2 border-r border-zinc-200 flex items-center gap-1.5">
                    <button data-testid={`gantt-toggle-${ws.name}`} onClick={() => setCollapsed({ ...collapsed, [ws.name]: !isCollapsed })}
                      className="text-zinc-400 hover:text-[#002FA7] shrink-0">
                      {isCollapsed ? <ChevronRight size={13} strokeWidth={1.5} /> : <ChevronDown size={13} strokeWidth={1.5} />}
                    </button>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium truncate">{ws.name}</p>
                      <p className="text-[10px] font-mono text-zinc-400">{fmtH(sum?.totalHours || 0)} hrs · {fmtC(sum?.totalCost || 0)}</p>
                    </div>
                  </div>
                  <div className="relative h-11" style={gridBg}>
                    {todayLine}
                    <div data-testid={`gantt-bar-${ws.name}`}
                      className="absolute top-1/2 -translate-y-1/2 h-6 rounded-sm flex items-center text-white shadow-sm"
                      style={{ left: (eff.start - 1) * CELL + 2, width: span * CELL - 4, backgroundColor: color }}>
                      <div data-testid={`gantt-handle-left-${ws.name}`} onPointerDown={(e) => beginDrag(e, ws, "left")}
                        className="w-2.5 h-full cursor-ew-resize flex items-center justify-center shrink-0 hover:bg-white/20 rounded-l-sm">
                        <span className="w-0.5 h-3 bg-white/70 rounded" />
                      </div>
                      <div onPointerDown={(e) => beginDrag(e, ws, "move")}
                        className="flex-1 h-full cursor-grab active:cursor-grabbing flex items-center gap-1 px-1 overflow-hidden">
                        {span * CELL > 90 && <GripVertical size={11} className="opacity-60 shrink-0" />}
                        <span className="text-[10px] font-mono whitespace-nowrap">W{eff.start}–W{eff.end}</span>
                      </div>
                      <div data-testid={`gantt-handle-right-${ws.name}`} onPointerDown={(e) => beginDrag(e, ws, "right")}
                        className="w-2.5 h-full cursor-ew-resize flex items-center justify-center shrink-0 hover:bg-white/20 rounded-r-sm">
                        <span className="w-0.5 h-3 bg-white/70 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* phase rows */}
                {!isCollapsed && segs.map((seg) => (
                  <div key={seg.name} className="flex bg-zinc-50/50">
                    <div className="w-64 shrink-0 pl-9 pr-3 py-1 border-r border-zinc-200 flex items-baseline justify-between gap-2">
                      <p className="text-[11px] text-zinc-600 truncate">{seg.name}</p>
                      <p className="text-[10px] font-mono text-zinc-400 shrink-0">{fmtH(seg.hours)}h</p>
                    </div>
                    <div className="relative h-7" style={gridBg}>
                      {todayLine}
                      <div data-testid={`gantt-phase-${ws.name}-${seg.name}`}
                        className="absolute top-1/2 -translate-y-1/2 h-3 rounded-sm"
                        style={{
                          left: ((eff.start - 1) + seg.f0 * span) * CELL + 2,
                          width: Math.max(6, (seg.f1 - seg.f0) * span * CELL - 4),
                          backgroundColor: color, opacity: 0.4,
                        }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
